import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { storeReturnContext } from './blogReturnPage';
import { toTitleCase } from '../../utils/casing';

// eslint-disable-next-line prettier/prettier
export default function BlogCard({
  title,
  image,
  date,
  blurb,
  slug,
  category,
  sourceCategory,
}) {
  const postHref = `/blog/${category}/${slug}`;
  // sourceCategory is the listing the card is rendered on (may be "all",
  // distinct from the post's own category) — only set from BlogList, so
  // clicking a related-post card elsewhere leaves any existing context alone.
  // The literal current URL is captured (not reconstructed later) so it
  // always matches exactly — trailing slash, ?Year= filters and all.
  const handleClick = () => {
    if (!sourceCategory || typeof window === 'undefined') return;
    storeReturnContext(
      toTitleCase(sourceCategory),
      window.location.pathname + window.location.search
    );
  };

  return (
    <Card
      sx={{
        maxWidth: 380,
        width: { xs: '100%', sm: 'auto' },
        height: 500,
        borderRadius: '32px',
        overflow: 'hidden',
        border: 'none',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        filter:
          'drop-shadow(0px 1px 2px rgba(162, 172, 177, 0.12)) drop-shadow(0px 6px 16px rgba(22, 47, 59, 0.12))',
        position: 'relative',
        mx: { xs: 'auto', sm: 0 },
      }}
    >
      {/* Card image — flexShrink: 0 keeps this locked at 220px. Without it,
          a flex column with a fixed container height (Card is height:500)
          will shrink whichever child has the least content to make room for
          the others; since CardMedia has no text, it's the first to give,
          so a 3-line title elsewhere would squeeze the image shorter. */}
      <CardMedia
        sx={{ height: 220, flexShrink: 0 }}
        image={image}
        title="green iguana"
      />

      {/* Date */}
      <Box
        sx={{
          mt: 2,
          px: 5,
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{date}</Typography>
      </Box>

      {/* Content */}
      <CardContent sx={{ px: 5, flexGrow: 1 }}>
        <a
          href={postHref}
          onClick={handleClick}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              cursor: 'pointer',
              // Reserved height is tied to the same lineHeight used for the
              // clamp, so the box is always exactly 3 lines tall regardless
              // of whether the title actually wraps to 1, 2 or 3 lines —
              // otherwise "Read More" below drifts up/down per card.
              lineHeight: 1.3,
              height: '3.9em',
            }}
            gutterBottom
            variant="h6"
          >
            {title}
          </Typography>
        </a>
        <Typography
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            // Fixed to the same 3-line reserved height, regardless of title
            // length — previously this was 3 or 4 lines depending on the
            // title's character count, which is only a rough proxy for how
            // many lines it actually renders, so the two boxes' combined
            // height varied slightly card to card.
            lineHeight: 1.4,
            height: '4.2em',
          }}
          variant="body2"
        >
          {blurb}
        </Typography>
      </CardContent>

      {/* Read More pinned to bottom */}
      <a
        href={postHref}
        onClick={handleClick}
        style={{ textDecoration: 'none', width: '100%' }}
      >
        <Box
          disableRipple
          sx={{
            mt: 'auto',
            flexShrink: 0,
            px: 5,
            py: 2,
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              color: '#162F3B;',
              cursor: 'pointer',
            }}
          >
            Read More
          </Typography>
        </Box>
      </a>
    </Card>
  );
}
